import { useEffect, useState } from 'react';
import { listMyPosts, type FeedPost } from './feed';
import { heldPrints } from './reveal';
import { storyOpened } from './storyboard';

/**
 * What the crew are developing, and what is dry and waiting to be turned over
 * (owner, 2026-09-28): an exercise's print, or a told story's storyboard. Two
 * places knock about it — the dot on the Today tab, and the road's stops,
 * which show a print developing or dry where the ✓ would be. Neither ever
 * shows the picture: it is face-down until your tap.
 *
 * Deliberately light: one read of your posts when a screen asks (shared for a
 * few seconds between screens), and a slow look again only while something
 * made a moment ago is still developing.
 */

const FRESH_MS = 48 * 3600 * 1000;
/** a render or storyboard still "pending" after this long is stuck, not developing */
const DEVELOPING_MS = 20 * 60 * 1000;
const REUSE_MS = 10 * 1000;
const POLL_MS = 15 * 1000;
const MAX_POSTS = 40;

const STORY_SETTLED = ['done', 'failed', 'too-short'];

/** A told story, storyboarded lately and not opened on this device. */
function storyDry(p: FeedPost, now: number): boolean {
  return !!p.storyId && p.storyboardStatus === 'done' && now - p.createdAt < FRESH_MS && !storyOpened(p.storyId);
}

/** Is the crew still working on this post, made a moment ago? */
function isDeveloping(p: FeedPost, now: number): boolean {
  return now - p.createdAt < DEVELOPING_MS && (p.renderStatus === 'pending' || (!!p.storyboardStatus && !STORY_SETTLED.includes(p.storyboardStatus)));
}

/** Prints not turned over, plus fresh storyboards not yet opened. */
export function waitingCount(posts: FeedPost[], now = Date.now()): number {
  return heldPrints(posts).length + posts.filter((p) => storyDry(p, now)).length;
}

/** Is the crew still working on something made a moment ago? */
export function developing(posts: FeedPost[], now = Date.now()): boolean {
  return posts.some((p) => isDeveloping(p, now));
}

/** A road stop's print: still in the darkroom, or dry and waiting at `to`. */
export type StopPrint = { state: 'developing' } | { state: 'dry'; to: string };

/** Each exercise's print, by challenge id: a dry one anywhere wins over one still developing. */
export function printsByExercise(posts: FeedPost[], now = Date.now()): Record<string, StopPrint> {
  const out: Record<string, StopPrint> = {};
  const held = new Set(heldPrints(posts).map((p) => p.postId));
  // newest first, so the first dry print found for an exercise is its newest
  for (const p of posts) {
    if (out[p.challengeId]?.state === 'dry') continue;
    if (held.has(p.postId)) out[p.challengeId] = { state: 'dry', to: `/print/${p.postId}` };
    else if (storyDry(p, now)) out[p.challengeId] = { state: 'dry', to: `/story/${p.storyId}` };
    else if (!out[p.challengeId] && isDeveloping(p, now)) out[p.challengeId] = { state: 'developing' };
  }
  return out;
}

let cache: { at: number; posts: Promise<FeedPost[]> } | null = null;
function recentPosts(fresh = false): Promise<FeedPost[]> {
  if (!fresh && cache && Date.now() - cache.at < REUSE_MS) return cache.posts;
  const posts = listMyPosts(MAX_POSTS);
  cache = { at: Date.now(), posts };
  posts.catch(() => { cache = null; });
  return posts;
}

/** Your recent posts, looked at again every so often while any is developing; null while loading or offline. */
export function useWatchedPosts(on = true): FeedPost[] | null {
  const [posts, setPosts] = useState<FeedPost[] | null>(null);
  useEffect(() => {
    if (!on) return;
    let stop = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const look = (fresh: boolean) => {
      recentPosts(fresh).then((ps) => {
        if (stop) return;
        setPosts(ps);
        if (developing(ps)) timer = setTimeout(() => look(true), POLL_MS);
      }).catch(() => { /* offline: nothing to knock about */ });
    };
    look(false);
    return () => { stop = true; clearTimeout(timer); };
  }, [on]);
  return posts;
}

/** How many things are dry and waiting on Today; 0 while loading or offline. */
export function useTodayWaiting(on = true): number {
  const posts = useWatchedPosts(on);
  return posts ? waitingCount(posts) : 0;
}
